import { formatPhone, type MeDto, type Page, PARTY_KINDS, type PartyListItemDto } from '@bqcrm/shared';
import { Avatar, Button, Card, Group, Modal, Pagination, Table, Text, TextInput } from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { IconAddressBook, IconPlus, IconSearch } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api } from '../../api';
import { can } from '../../auth';
import { PageHeader } from '../../components/PageHeader';
import { QueryState } from '../../components/QueryState';
import { EmptyState, FilterChips } from '../../components/ui';
import { PartyForm } from './PartyForms';

const PAGE = 50;

export function PartiesPage({ me }: { me: MeDto }) {
  const [q, setQ] = useState('');
  const [debounced] = useDebouncedValue(q, 300);
  const [kind, setKind] = useState('all');
  const [page, setPage] = useState(1);
  const [opened, modal] = useDisclosure();
  const navigate = useNavigate();
  const params = new URLSearchParams({ limit: String(PAGE), offset: String((page - 1) * PAGE) });
  if (debounced.trim()) params.set('q', debounced.trim());
  if (kind !== 'all') params.set('kind', kind);
  const list = useQuery({ queryKey: ['parties', params.toString()], queryFn: () => api<Page<PartyListItemDto>>(`/parties?${params}`) });

  return (
    <>
      <PageHeader
        title="Klienci"
        description="Wspólna baza klientów wszystkich firm: osoby prywatne i firmy. Ten sam klient może być zleceniodawcą, inwestorem lub płatnikiem w różnych sprawach."
        actions={
          can(me, 'klienci.edycja') && (
            <Button leftSection={<IconPlus size={16} />} onClick={modal.open}>
              Nowy klient
            </Button>
          )
        }
      />
      <Group mb="md" wrap="wrap">
        <TextInput
          placeholder="Szukaj: nazwisko, nazwa, telefon, e-mail, NIP, miejscowość…"
          leftSection={<IconSearch size={16} />}
          value={q}
          onChange={(e) => {
            setQ(e.currentTarget.value);
            setPage(1);
          }}
          w={{ base: '100%', sm: 380 }}
        />
        <FilterChips
          value={kind}
          onChange={(v) => {
            setKind(v);
            setPage(1);
          }}
          options={[{ value: 'all', label: 'Wszyscy' }, ...Object.entries(PARTY_KINDS).map(([value, label]) => ({ value, label }))]}
        />
      </Group>
      <Card padding={0}>
      <QueryState isLoading={list.isLoading} error={list.error}>
        {list.data?.items.length === 0 ? (
          <EmptyState icon={<IconAddressBook size={24} />} title="Brak klientów" hint={debounced ? 'Nic nie pasuje do wyszukiwania — sprawdź pisownię albo szukaj po telefonie.' : 'Dodaj pierwszego klienta przyciskiem „Nowy klient”.'} />
        ) : (
        <Table.ScrollContainer minWidth={760}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Klient</Table.Th>
                <Table.Th>Kontakt</Table.Th>
                <Table.Th>Miejscowość</Table.Th>
                <Table.Th ta="right">Lokalizacje</Table.Th>
                <Table.Th ta="right">Sprawy</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {list.data?.items.map((p) => (
                <Table.Tr key={p.id} className="row-link" onClick={() => navigate(`/klienci/${p.id}`)}>
                  <Table.Td>
                    <Group gap="sm" wrap="nowrap">
                      <Avatar size={34} radius="xl" color={p.kind === 'firma' ? 'blue' : 'eko'} variant="light">
                        {p.displayName
                          .split(' ')
                          .filter(Boolean)
                          .slice(0, 2)
                          .map((x) => x[0])
                          .join('')
                          .toUpperCase()}
                      </Avatar>
                      <div>
                        <Text component={Link} to={`/klienci/${p.id}`} fw={600} size="sm" c="var(--app-link)" onClick={(e) => e.stopPropagation()}>
                          {p.displayName}
                        </Text>
                        {p.kind === 'firma' && (
                          <Text size="xs" c="dimmed">
                            firma{p.nip ? ` · NIP ${p.nip}` : ''}
                          </Text>
                        )}
                      </div>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{p.phones.map(formatPhone).join(', ') || '—'}</Text>
                    <Text size="xs" c="dimmed">
                      {p.emails.join(', ')}
                    </Text>
                  </Table.Td>
                  <Table.Td>{p.city || '—'}</Table.Td>
                  <Table.Td ta="right" className="tabular">
                    {p.sitesCount}
                  </Table.Td>
                  <Table.Td ta="right" className="tabular">
                    {p.casesCount}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        )}
        <Group justify="space-between" px="md" py="sm" style={{ borderTop: '1px solid var(--app-border)' }}>
          <Text size="sm" c="dimmed">
            Klientów: {list.data?.total ?? 0}
          </Text>
          <Pagination size="sm" total={Math.max(1, Math.ceil((list.data?.total ?? 0) / PAGE))} value={page} onChange={setPage} />
        </Group>
      </QueryState>
      </Card>
      <Modal opened={opened} onClose={modal.close} title="Nowy klient" size="lg">
        <PartyForm
          party={null}
          onCancel={modal.close}
          onDone={(p) => {
            modal.close();
            navigate(`/klienci/${p.id}`);
          }}
        />
      </Modal>
    </>
  );
}
